// Session data hooks: /me (roles, licence classes), /api/v1/meta polled every 60 s, role checks, action availability (read-only, job launcher) and the job launcher's name and worker-offline note.
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";

import type { ApiMeta, Me, Role } from "@/api/__generated__";
import { metaGetMeOptions, metaGetMetaOptions } from "@/api/__generated__/@tanstack/react-query.gen";
import { canAccess, type AccessLevel } from "@/app/nav";

export const META_POLL_MS = 60_000;

export function meQueryOptions() {
  return { ...metaGetMeOptions(), staleTime: 10 * 60_000 };
}

export function metaQueryOptions() {
  return { ...metaGetMetaOptions(), refetchInterval: META_POLL_MS, staleTime: 30_000 };
}

export function useMe(): Me {
  return useSuspenseQuery(meQueryOptions()).data;
}

export function useRoles(): readonly Role[] {
  return useMe().roles;
}

export function useCanAccess(level: AccessLevel): boolean {
  return canAccess(useRoles(), level);
}

export function useApiMeta(): ApiMeta | undefined {
  return useQuery(metaQueryOptions()).data;
}

export interface ActionAvailability {
  disabled: boolean;
  reason: string | null;
}

export function actionAvailability(meta: ApiMeta | undefined, requireLauncher: boolean): ActionAvailability {
  if (meta === undefined) {
    return { disabled: true, reason: "Checking whether actions are available…" };
  }
  if (meta.read_only) {
    return { disabled: true, reason: "This server runs in read-only mode, so actions are turned off." };
  }
  if (requireLauncher && meta.job_launcher === "none") {
    return { disabled: true, reason: "No job launcher is configured on this server." };
  }
  return { disabled: false, reason: null };
}

export function useActionAvailability(requireLauncher = false): ActionAvailability {
  return actionAvailability(useApiMeta(), requireLauncher);
}

export type LauncherKind = ApiMeta["job_launcher"];

export const LAUNCHER_LABELS: Record<LauncherKind, string> = {
  local: "Local processes",
  worker: "Worker service",
  aca: "Azure Container Apps jobs",
  none: "No job launcher",
};

export const WORKER_OFFLINE_NOTE = "No worker is online; jobs will queue until one connects.";

export function launcherNote(meta: ApiMeta | undefined): string | null {
  if (meta?.job_launcher === "worker" && meta.workers_online === 0) {
    return WORKER_OFFLINE_NOTE;
  }
  return null;
}

export function useLauncherNote(): string | null {
  return launcherNote(useApiMeta());
}
